import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Box,
  InputLabel,
  styled,
  CardActions,
} from "@mui/material";
import internshipIcon from "../../assets/img/internshipIcon.png";
import { COLORS } from "colors/color";
import useScoreStatistics from "hooks/useScoreStatistics";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

const StyledSelect = styled(Select)(({ theme, isediting }) => ({
  height: 56,
  fontFamily: "Poppins",
  borderRadius: "10px",
  width: "100%",
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "2px",
    boxShadow: "0px 0px 5px 0.4px #0085FF",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "2px",
    boxShadow: "0px 0px 5px 0.4px #0085FF",
  },
  "& .MuiSelect-select": {
    padding: "16.5px 14px",
  },
}));

const customMenuProps = {
  PaperProps: {
    sx: {
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "linear-gradient(45deg, #0085FF, #004F98)",
        borderRadius: "4px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "linear-gradient(45deg, #004F98, #002B5C)",
      },
    },
  },
};


const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "none",
  transition: "box-shadow 0.3s ease-in-out",
  height: "100%",
  border: '1px solid #D9D9D9',
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  },
}));

const CardHeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const CardBodyWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  height: { xs: 300, sm: 400, md: 500 },
}));

const PostScoreStatistics = ({ userId, apiUrl }) => {
  const { t } = useTranslation();
  const {
    postsData,
    selectedPost,
    setSelectedPost,
    chartData,
    chartOptions,
    loading,
    error,
    colors,
  } = useScoreStatistics(userId, apiUrl);

  if (loading) return <Typography sx={{ p: 2 }}>{t("general.loading")}</Typography>;
  if (error)
    return (
      <Typography sx={{ p: 2, color: "error.main" }}>
        {t("general.error", { message: error })}
      </Typography>
    );

  const handleChange = (event) => {
    setSelectedPost(event.target.value);
  };

  return (
    <StyledCard sx={{ maxWidth: "xl" }}>
      <CardHeaderWrapper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Box display="flex" alignItems="center">
            {/* <img src={internshipIcon} alt={t("postScoreChart.postScoreStatistics.iconAlt")} /> */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: { xs: '14px', sm: '16px' },
                color: '#00000080'
              }}
            >
              {t("postScoreChart.postScoreStatistics.title")}
            </Typography>
          </Box>

          <FormControl
            fullWidth
            size="small"
            sx={{
              width: "auto",
              height: { xs: 28, sm: 32 },
              borderRadius: '8px',
              borderWidth: '0.5px',
              paddingTop: { xs: '3px', sm: '5px' },
              paddingRight: { xs: '10px', sm: '15px' },
              paddingBottom: '0px',
              paddingLeft: { xs: '10px', sm: '15px' },
              gap: '8px',
            }}
          >
            {/* <InputLabel id="post-select-label">
              {t("postScoreChart.postScoreStatistics.selectPost")}
            </InputLabel> */}
            <StyledSelect
              labelId="post-select-label"
              id="post-select"
              value={selectedPost}
              label={t("postScoreChart.postScoreStatistics.selectPost")}
              onChange={handleChange}
              MenuProps={customMenuProps}
            >
              {Object.keys(postsData).map((postId) => (
                <MenuItem key={postId} value={postId}>
                  {t("postScoreChart.postScoreStatistics.postLabel", { number: postId })}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Box>
      </CardHeaderWrapper>

      <CardBodyWrapper
        sx={{
          height: { xs: 250, sm: 320 },
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "100%", height: "100%", maxWidth: 400 }}>
          <Pie data={chartData} options={chartOptions} />
        </Box>
      </CardBodyWrapper>
      <CardActions
        sx={{ borderTop: 1, borderColor: "divider", px: 2, py: 1.5 }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
          {chartData.labels.map((label, index) => (
            <div
              key={label}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: colors[index],
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              />
              <Typography variant="body2">
                {label} ({chartData.percentages?.[index] || 0}%)
              </Typography>
            </div>
          ))}
        </div>
      </CardActions>
    </StyledCard>
  );
};

export default PostScoreStatistics;